import React, { useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { duplicarRodada } from "entity/Rodada/duplicarRodada";
import useNotification from "notification";
import ConfirmDialog from "components/dialog/ConfirmDialog";
import WindowDialog from "components/dialog/WindowDialog";

/**
 * Componente reutilizável para duplicar uma rodada completa (rodada + inscritos).
 *
 * Fluxo:
 * 1. Exibe ConfirmDialog perguntando se deseja duplicar
 * 2. Mostra loading durante a duplicação
 * 3. Exibe feedback com resultado
 * 4. Se showRedirect=true, pergunta se quer visualizar a nova rodada
 *
 * @param {object} rodada - Objeto completo da rodada a duplicar
 * @param {boolean} open - Controla a abertura do dialog
 * @param {function} onClose - Callback ao fechar (recebe { duplicated: bool, rodadaUid?: string } )
 * @param {boolean} showRedirect - Se true, mostra modal perguntando se quer ir pra nova rodada
 */
export default function DuplicarRodadaDialog({ rodada, open, onClose, showRedirect = false }) {
    const { showSuccess, showError } = useNotification();

    const [loading, setLoading] = useState(false);
    const [showRedirectDialog, setShowRedirectDialog] = useState(false);
    const [novaRodadaUid, setNovaRodadaUid] = useState(null);

    const handleConfirmar = async (confirmou) => {
        if (!confirmou) {
            onClose({ duplicated: false });
            return;
        }

        setLoading(true);

        try {
            const resultado = await duplicarRodada(rodada);

            setNovaRodadaUid(resultado.rodadaUid);

            let msg = `Rodada "${resultado.rodadaNome}" criada com sucesso`;
            if (!resultado.inscritos.semInscritos) {
                msg += `. ${resultado.inscritos.copiados} inscrito(s) copiado(s)`;
                if (resultado.inscritos.ignorados > 0) {
                    msg += `, ${resultado.inscritos.ignorados} ignorado(s)`;
                }
            }
            showSuccess(msg);

            setLoading(false);

            if (showRedirect) {
                setShowRedirectDialog(true);
            } else {
                onClose({ duplicated: true, rodadaUid: resultado.rodadaUid });
            }
        } catch (error) {
            console.error("Erro ao duplicar rodada:", error);
            showError("Erro ao duplicar rodada. Tente novamente.");
            setLoading(false);
            onClose({ duplicated: false });
        }
    };

    const handleRedirect = (confirmou) => {
        setShowRedirectDialog(false);
        onClose({
            duplicated: true,
            rodadaUid: novaRodadaUid,
            redirect: confirmou === true,
        });
    };

    if (!open || !rodada) return null;

    return (
        <>
            {/* Loading dialog */}
            {loading && (
                <WindowDialog
                    title="Duplicando Rodada"
                    open={true}
                    maxWidth="xs"
                    hideCloseButton
                >
                    {() => (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", p: 4, gap: 2 }}>
                            <CircularProgress />
                            <Typography>Duplicando rodada e copiando inscritos, aguarde...</Typography>
                        </Box>
                    )}
                </WindowDialog>
            )}

            {/* Dialog de redirecionamento */}
            {showRedirectDialog && (
                <ConfirmDialog
                    title="Rodada Duplicada"
                    open={true}
                    onClose={handleRedirect}
                    labelYes="Sim, visualizar"
                    labelNo="Não"
                >
                    Rodada duplicada com sucesso! Deseja visualizar a nova rodada?
                </ConfirmDialog>
            )}

            {/* Dialog de confirmação inicial */}
            {!loading && !showRedirectDialog && (
                <ConfirmDialog
                    title="Duplicar Rodada"
                    open={true}
                    onClose={handleConfirmar}
                    labelYes="Sim, duplicar"
                    labelNo="Cancelar"
                >
                    Deseja duplicar a rodada <strong>{rodada.nome}</strong>?
                    <br />
                    <Typography variant="caption" sx={{ mt: 1, display: "block", color: "text.secondary" }}>
                        Todos os dados da rodada e seus inscritos serão copiados para uma nova rodada.
                    </Typography>
                </ConfirmDialog>
            )}
        </>
    );
}
